import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { UserService } from "@main-services/user.service";
import { Helper } from "@helpers/functions";
import { ValidationError } from "@models";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { InfoService } from "@core-services/info.service";
import { TranslationService } from "@core-services/translation.service";
import { NewProfileFacade } from "@modules/main/components/profile/new-profile/state/new-profile.facade";
import { ProfileModel } from "@modules/main/components/profile/new-profile/entities/profile-model";
import { maskMobilePhoneNumber } from "@helpers/utils";

@Component({
	selector: "verification-sms-code",
	templateUrl: "./verification-sms-code.component.html",
	styleUrls: ["./verification-sms-code.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class VerificationSmsCodeComponent implements OnInit {
	@Input()
	public codeLength: number = 0;
	@Input()
	public password: string = "";
	@Output()
	public pinSent: EventEmitter<string> = new EventEmitter();
	public inputValues: string[] = [];
	// Send to API
	public inputArray: string[] = [];
	public codeLength_: number[] = [];
	public profile$ = this.profileFacade.getProfile$();

	public constructor(
		protected apicallService: ApicallService,
		protected translationService: TranslationService,
		protected infoService: InfoService,
		protected userService: UserService,
		protected profileFacade: NewProfileFacade
	) {}

	public ngOnInit(): void {
		// Set the codeLength_ array used to generate inputs
		this.codeLength_ = Array.from(Array(this.codeLength).keys());
	}
	// Check if the input is in the correct format
	public correctInput(): boolean {
		return this.inputArray.length === this.codeLength && !this.inputArray.some((item) => isNaN(+item));
	}

	public getUserPhone(profile: ProfileModel): string {
		return maskMobilePhoneNumber(profile.mobile);
	}

	// Separate request for new sms code
	public resendPin(): void {
		const argumentsObj = {
			password: this.password,
			used_for: "activate"
		};
		const url = "user/verify2FA";
		const options = new ApicallOption(null, argumentsObj);

		this.apicallService.post(url, options).subscribe(
			() => {
				this.infoService.setSuccess(this.translationService.translate("info_text_verify_pin_code_sent"));
			},
			(e) => {
				if (
					e instanceof ValidationError &&
					!!e.responseErrors &&
					!!e.responseErrors[0] &&
					e.responseErrors[0].code === "2fa.de_activation.sms.verification.required"
				) {
					return;
				} // Skip showing this specific error
				Helper.handleError(e);
			}
		);
		this.resetInput();
	}

	public getInputAsSingleString(): string {
		return this.inputArray.join("");
	}

	public isNumberKey(event): void {
		// Blocks the event to propagate further if no numbers are typed in
		if (isNaN(Number(event.key)) || event.target.value.length > 0) {
			const ctrlKey = !!event.ctrlKey || !!event.metaKey; // Meta key for Mac users
			const charCode = event.which || event.key;
			if (!ctrlKey && charCode !== 86) {
				event.preventDefault();
				event.stopPropagation();
			}
		}
	}

	public keytab(event, digit: number): void {
		let element;

		if (event.code === "Backspace") {
			if (this.inputArray[digit]) {
				this.inputArray[digit] = "";
				this.inputValues[digit] = "";
			} else {
				element = event.target.previousElementSibling;
				if (+this.inputArray[digit] === 0) {
					this.inputArray[digit] = "";
					this.inputValues[digit] = "";
				}
			}
		} else {
			this.inputArray[digit] = this.inputValues[digit];
			if (!!this.inputArray[digit] || +this.inputArray[digit] === 0) {
				element = event.target.nextElementSibling;
			}
		}
		if (this.correctInput()) {
			this.pinSent.emit(this.getInputAsSingleString());
		}
		if (!element) {
			return;
		}
		element.focus();
	}

	public resetInput(): void {
		// Reset input array
		this.inputValues = [];
		this.inputArray = [];

		// Reset focus on first input
		const element = document.getElementById("id_0");
		element.focus();
	}

	public onPaste(event: ClipboardEvent): void {
		event.preventDefault();
		event.stopPropagation();

		const data = event.clipboardData ? event.clipboardData.getData("text").trim() : undefined;
		// Convert pasted text into iterable
		const values = data?.split("");
		// Do nothing if pasted message is too long
		if (values.length > this.codeLength) {
			return;
		}
		this.inputValues = values;
		// Set array sent to API and emit code
		this.inputArray = this.inputValues;
		if (this.correctInput()) {
			this.pinSent.emit(this.getInputAsSingleString());
		}
	}
}
